import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { ApiError, newIdempotencyKey } from '@/api/http';
import { explainLine } from '@/api/explain';
import { Confirm } from '@/components/Confirm';
import { ToolIcon } from '@/components/ToolIcon';
import { useAuth } from '@/auth/auth';
import { can, cannotBecause } from '@/auth/can';

/**
 * A reviewer's attestation that a candidate detection is really this tool.
 *
 * Promotion is the hinge nobody could reach. A placement rule keys on a
 * supported detection, so nothing could be placed and every package was empty,
 * and the only way to promote was a database session. The route authorises the
 * person; the database still judges the evidence, and refuses with a 400 that
 * names which bar failed.
 *
 * That refusal is worth rendering whole. The entire reason it is a 400 rather
 * than a 503 is that it says what to fix, and flattening it to "forbidden"
 * throws away the only useful thing about it.
 *
 * It is irreversible in the sense that matters: a promotion records a verified
 * person against a detection, so it asks before it fires.
 */

export interface Candidate {
  id: string;
  toolId: string;
  display: { label: string; iconSlug?: string | null; domain?: string | null; brandColor?: string | null };
  evidence: { signals: number; families: number };
}

/** The recalibrated bar: ten independent signals across two evidence families. */
export const SIGNALS_REQUIRED = 10;
export const FAMILIES_REQUIRED = 2;

export function PromoteDetection({ bucketId, candidates }: { bucketId: string; candidates: Candidate[] }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const mayReview = can(user?.role, 'review');
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ id: string; message: string; ok: boolean } | null>(null);

  const promote = async (c: Candidate) => {
    setBusy(c.id);
    setNote(null);
    try {
      await backend.promoteToolDetection(bucketId, c.id, newIdempotencyKey());
      setNote({ id: c.id, ok: true, message: `${c.display.label} is confirmed. Its files can be placed in a package now.` });
      void qc.invalidateQueries({ queryKey: ['toolset'] });
      void qc.invalidateQueries({ queryKey: ['company-tools'] });
    } catch (e) {
      // The database names the bar that failed. That sentence is the whole
      // value of the refusal, so it is shown rather than replaced.
      const named = e instanceof ApiError && e.code === 'INVALID_REQUEST' ? (e.reason ?? e.message) : null;
      setNote({ id: c.id, ok: false, message: named ?? explainLine(e, 'confirm this tool') });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      {candidates.map((c) => {
        const short = c.evidence.signals < SIGNALS_REQUIRED || c.evidence.families < FAMILIES_REQUIRED;
        return (
          <div key={c.id} className="flex flex-wrap items-center gap-2 rounded border border-line bg-panel-2 px-2 py-1.5">
            <ToolIcon tool={c.toolId} display={c.display} size={13} />
            <span className="text-caption text-ink-2">{c.display.label}</span>
            <span className="text-caption text-ink-4">
              {c.evidence.signals.toLocaleString()}/{SIGNALS_REQUIRED} signals ·{' '}
              {c.evidence.families}/{FAMILIES_REQUIRED} kinds of check
            </span>
            <span className="ml-auto flex items-center gap-2">
              {/* Short of the bar the database will refuse, so the button says
                  so rather than inviting a press that answers 400. */}
              {short ? (
                <span className="text-caption text-ink-4">not enough evidence yet</span>
              ) : (
                <Confirm
                  label="Confirm this tool"
                  confirmLabel={`Confirm ${c.display.label}`}
                  consequence="This records you as the person who attested to it."
                  busyLabel="Confirming…"
                  loading={busy === c.id}
                  disabled={!mayReview || busy !== null}
                  onConfirm={() => void promote(c)}
                />
              )}
            </span>
            {note?.id === c.id && (
              <span
                role="status"
                className={
                  'w-full rounded border px-2 py-1 text-caption ' +
                  (note.ok ? 'border-ok/40 bg-ok-soft text-ink-2' : 'border-warn/40 bg-warn-soft text-ink-2')
                }
              >
                {note.message}
              </span>
            )}
          </div>
        );
      })}
      {!mayReview && candidates.length > 0 && (
        <span className="text-caption text-ink-3">{cannotBecause(user?.role, 'review')}</span>
      )}
    </div>
  );
}
